import React, { useRef, useMemo, useEffect } from "react";
import { gsap } from "gsap";
import { FileText, Book } from "lucide-react";
import LocalIcon from "./icons/LOCAL";
import EllipseIcon from "./icons/Ellipse";

const HeroSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<HTMLDivElement[]>([]);

  const particleCount = 40;
  const lightColors = useMemo(() => ["#eaf9d9", "#FFFFFF"], []);
  const colors = useMemo(
    () => ["#00856D", "#4059ad", "#FFFFFF", "#F97068", "#FFFFFF", "#FFFFFF"],
    []
  );
  const sizes = [70, 100, 150, 175];

  const generateRandomPositions = useMemo(() => {
    const positions = [];
    const sectors = 3;
    const particlesPerSector = Math.ceil(particleCount / (sectors * sectors));

    for (let i = 0; i < sectors; i++) {
      for (let j = 0; j < sectors; j++) {
        for (let k = 0; k < particlesPerSector; k++) {
          if (positions.length < particleCount) {
            positions.push({
              x: (i / sectors) * 100 + Math.random() * (100 / sectors),
              y: (j / sectors) * 100 + Math.random() * (100 / sectors),
            });
          }
        }
      }
    }
    return positions;
  }, [particleCount]);

  const distributeColors = useMemo(() => {
    const distributedColors = [];
    for (let i = 0; i < particleCount; i++) {
      distributedColors.push(colors[i % colors.length]);
    }
    return distributedColors;
  }, [particleCount, colors]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    svg.innerHTML = "";

    setTimeout(() => {
      const particleElements = document.querySelectorAll(".particle");
      const containerRect = sectionRef.current?.getBoundingClientRect() || {
        left: 0,
        top: 0,
      };

      const lines: SVGLineElement[] = [];

      particleElements.forEach((particle, i) => {
        particleElements.forEach((target, j) => {
          if (i < j && Math.random() > 0.85) {
            const startRect = particle.getBoundingClientRect();
            const endRect = target.getBoundingClientRect();

            const line = document.createElementNS(
              "http://www.w3.org/2000/svg",
              "line"
            );

            const startX =
              startRect.left - containerRect.left + startRect.width / 2;
            const startY =
              startRect.top - containerRect.top + startRect.height / 2;
            const endX = endRect.left - containerRect.left + endRect.width / 2;
            const endY = endRect.top - containerRect.top + endRect.height / 2;

            const connectionColor =
              colors[Math.floor(Math.random() * colors.length)];

            line.setAttribute("x1", startX.toString());
            line.setAttribute("y1", startY.toString());
            line.setAttribute("x2", endX.toString());
            line.setAttribute("y2", endY.toString());
            line.setAttribute("stroke", connectionColor);
            line.setAttribute("stroke-width", "1");
            line.setAttribute("opacity", "0");

            svg.appendChild(line);
            lines.push(line);
          }
        });
      });

      gsap.to(lines, {
        opacity: 0.3,
        duration: 0.8,
        stagger: {
          amount: 0.5,
          from: "random",
        },
        delay: 1,
      });
    }, 100);
  }, [colors]);

  useEffect(() => {
    const logo = logoRef.current;
    const title = titleRef.current;
    const description = descriptionRef.current;
    const cta = ctaRef.current;
    const particles = particlesRef.current;

    if (!logo || !title || !description || !cta) return;

    gsap.set([logo, title, description, cta], { autoAlpha: 0, y: 50 });
    gsap.set(particles, { autoAlpha: 0, scale: 0.5 });

    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    tl.to(
      particles,
      {
        autoAlpha: 1,
        scale: 1,
        duration: 1,
        stagger: {
          amount: 0.5,
          from: "random",
        },
      },
      0
    )
      .to(logo, { autoAlpha: 1, y: 0, duration: 0.8 }, 0.2)
      .to(title, { autoAlpha: 1, y: 0, duration: 0.8 }, 0.4)
      .to(description, { autoAlpha: 1, y: 0, duration: 0.8 }, 0.6)
      .to(cta, { autoAlpha: 1, y: 0, duration: 0.8 }, 0.8);

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="hero-section relative h-screen min-h-screen flex items-center justify-center overflow-hidden"
    >
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(135deg, #eaf9d9, #FFFFFF)" }}
      />

      <svg
        ref={svgRef}
        className="absolute inset-0 w-full h-full pointer-events-none z-10"
      ></svg>

      <div className="absolute inset-0 z-20">
        {generateRandomPositions.map((position, i) => (
          <div
            key={i}
            ref={(el) => (particlesRef.current[i] = el as HTMLDivElement)}
            className="particle absolute"
            style={{
              top: `${position.y}%`,
              left: `${position.x}%`,
              transform: `translate(-50%, -50%)`,
              opacity: 0,
            }}
          >
            <EllipseIcon
              width={sizes[Math.floor(Math.random() * sizes.length)]}
              height={sizes[Math.floor(Math.random() * sizes.length)]}
              fill={distributeColors[i]}
              style={{
                filter: "blur(20px)",
                opacity: 1,
              }}
            />
          </div>
        ))}
      </div>

      <div className="absolute inset-0 backdrop-filter backdrop-blur-sm z-30"></div>

      <div className="max-w-screen-xl px-4 pt-20 pb-8 mx-auto relative z-40">
        <div className="flex flex-col items-center justify-center gap-10 mx-auto place-self-center text-center">
          <div ref={logoRef}>
            <LocalIcon className="w-[300px] h-9 lg:w-[500px] lg:h-36" />
          </div>

          <h1
            ref={titleRef}
            className="max-w-2xl md:max-w-3xl lg:max-w-[900px] text-4xl font-semibold md:font-medium leading-none tracking-tight md:text-5xl text-gray-900"
          >
            The Future of Decentralized Marketplaces
          </h1>

          <p
            ref={descriptionRef}
            className="max-w-2xl lg:max-w-3xl md:text-lg lg:text-xl font-medium text-gray-900 antialiased"
          >
            Empowering communities with a trustless, peer-to-peer marketplace
            that adapts to their needs. Unlock new possibilities with
            programmable, interoperable, and user-owned networks.
          </p>

          <div
            ref={ctaRef}
            className="space-y-4 flex flex-col sm:flex-row items-center justify-center sm:space-y-0 sm:space-x-4"
          >
            <a
              href="https://palette-labs-inc.github.io/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center px-8 py-3 text-lg font-medium text-white bg-gray-900 rounded-full hover:bg-gray-800 transition-colors duration-200"
            >
              <FileText className="w-6 h-6 mr-2" />
              White Paper
            </a>
            <a
              href="https://docs.localprotocol.xyz"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center px-8 py-3 text-lg font-medium border-2 border-gray-900 text-gray-900 rounded-full hover:bg-gray-100 transition-colors duration-200"
            >
              <Book className="w-6 h-6 mr-2" />
              Read the Docs
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
